import React from 'react';
import { useGetList } from 'react-admin';
import { Link } from 'react-router-dom';
import {
	Card,
	CardContent,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';

const PER_PAGE = 10;

// Encodé une fois : le lien de bas de carte doit ouvrir la liste avec le filtre
// exact du panneau, sinon les deux écrans ne montrent pas les mêmes comptes.
const NO_ACTIVITY_FILTER = encodeURIComponent(JSON.stringify({ state: 'noActivity' }));

// InactiveBillingAccounts : les comptes sans aucune activité depuis 30 jours.
//
// « Sans activité » = aucun traitement de connecteur ET aucune session MCP sur la
// période. Le calcul vit côté serveur (`noActivitySQL`), pour que ce panneau et
// le filtre de la liste ne puissent pas répondre différemment.
//
// ⚠️ On n'en montre que les 10 premiers, mais le total affiché est le VRAI total
// (`useGetList` le rend séparément de la page). Un panneau qui titrerait « 10 »
// en laisserait 27 dans l'ombre.
export const InactiveBillingAccounts: React.FC = () => {
	const { data, total, isLoading, error } = useGetList('billing-accounts', {
		pagination: { page: 1, perPage: PER_PAGE },
		sort: { field: 'created_at', order: 'DESC' },
		filter: { state: 'noActivity' },
	});

	const rest = (total ?? 0) - (data?.length ?? 0);

	return (
		<Card sx={{ height: '100%' }}>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Sans activité depuis 30 jours{typeof total === 'number' ? ` (${total})` : ''}
				</Typography>
				<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
					Aucun traitement de connecteur ni session MCP sur la période. Un paiement
					automatique ne compte pas comme une activité.
				</Typography>
				{error && (
					<Typography variant="body2" color="error">
						Chargement impossible
					</Typography>
				)}
				{isLoading && !error && (
					<Typography variant="body2" color="text.secondary">
						Chargement…
					</Typography>
				)}
				{!isLoading && !error && !data?.length && (
					<Typography variant="body2" color="text.secondary">
						Tous les comptes ont eu une activité récente
					</Typography>
				)}
				{!!data?.length && (
					<>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Compte</TableCell>
									<TableCell>Type</TableCell>
									<TableCell>Projets</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{data.map((ba: any) => (
									<TableRow key={ba.id} hover>
										<TableCell>
											<Link to={`/billing-accounts/${ba.id}`}>{ba.name || '(sans nom)'}</Link>
										</TableCell>
										<TableCell>
											{ba.billingMode === 'contract' ? (
												<Chip size="small" color="primary" label="Entreprise" />
											) : (
												<Chip size="small" label="Pay as you go" />
											)}
											{ba.isFree && (
												<Chip size="small" variant="outlined" label="Free" sx={{ ml: 0.5 }} />
											)}
										</TableCell>
										<TableCell>{ba.projectCount ?? 0}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
						<Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
							{/* Le MÊME filtre que ce panneau : la liste qui s'ouvre montre
							    exactement ces comptes, ni plus ni moins. */}
							<Link to={`/billing-accounts?filter=${NO_ACTIVITY_FILTER}`}>
								{rest > 0
									? `${rest} autre${rest > 1 ? 's' : ''} — voir la liste complète`
									: 'Voir la liste complète'}
							</Link>
						</Typography>
					</>
				)}
			</CardContent>
		</Card>
	);
};
